import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { pctTolerance } from '@/geo/simplify'
import { isSimple } from '@/geo/polygon'
import { snapPoint, snapSourcesFromRings, type SnapResult } from '@/geo/snap'
import { EDGE_H, EDGE_W, makeEdgeProbe, type BuildResponse, type EdgeMap } from '@/geo/edgeMap.worker'
import { ASPECT, VIEW_H, VIEW_W, type Pt, type Ring } from '@/geo/types'
import { useEditorStore, isStageBody, ringOf, type EditorDoc } from '@/stores/editorStore'
import { cn } from '@/components/ui/utils'
import type { StageBody } from '@/schema'

import { ProposalLayer } from './ProposalLayer'
import { AnchorMarker } from './shapes/AnchorMarker'
import { HorizonLine } from './shapes/HorizonLine'
import { LayerShape } from './shapes/LayerShape'
import { PathShape } from './shapes/PathShape'
import { PortalMarker } from './shapes/PortalMarker'
import { RegionShape } from './shapes/RegionShape'
import { ToolPreview } from './shapes/ToolPreview'
import { VertexHandles } from './shapes/VertexHandles'
import { DEFAULT_TOOL_CONFIG, type FeatureType, type HitTarget, type ToolEvent } from './tools/types'
import { reduce, TOOL_BY_ID, type ReduceContext } from './tools/reducer'
import { zoomAbout, zoomStep } from './tools/pan'
import { domainOfFeature, domainOfTool, featuresOf, kindColour, kindLabel, SLOT_OF, useVocab } from './vocab'

/**
 * The annotation canvas: **one** `<svg viewBox="0 0 100 56.25">`.
 *
 * The svg box MUST be 16:9 — `clientToPct`, the zoom / pan transform and every hit tolerance
 * read the box width as 100 % and its height as 16:9 of it. The parent guarantees that
 * (`FitPicture`); in a taller box the picture was letterboxed while clicks were still read
 * against the whole box, so every point landed too low or too high.
 *
 * Coordinates. Everything stored is a percent of the plate (§4.0), so the
 * viewBox is the percent space squashed to 16:9 — `x` is percent-of-width
 * unchanged, `y` is percent-of-height × 0.5625 (`pctToView` in `geo/types`).
 * One conversion, in one place, and every shape component is free of aspect
 * arithmetic.
 *
 * Pointers. All handlers live on the root `<svg>` with `setPointerCapture`,
 * children only stamp `data-id` / `data-vertex` / `data-edge`, and
 * `clientToPct` implements §9.2 exactly:
 * `img_px = ((clientX − rect.left) − tx) / k`, `pct = img_px / W × 100`.
 *
 * Behaviour. The canvas owns no interaction logic at all — it converts DOM
 * events into `ToolEvent`s, runs the pure reducer from `tools/reducer.ts`
 * and hands the resulting effects to `editorStore`. That is why a drag is
 * one undo step and why the whole thing is testable without a browser.
 */

export interface AnnotationCanvasProps {
  /** Same-origin media URL for the plate (`/api/media/{uid}/preview.webp`). */
  plateUrl?: string
  /** Live plate: render a `<video>` instead of an `<image>`. */
  video?: boolean
  /** Current scrub position for a live plate, seconds. */
  videoTime?: number
  className?: string
  /** Hide the tool preview and the handles (Simple fix mode uses this). */
  minimal?: boolean
  /** Called with the percent position of the pointer, for the status bar and the loupe. */
  onCursor?: (pt: Pt | null) => void
  /** `Space` is held: pan is armed. */
  panArmed?: boolean
}

/** §9.2 screen-pixel thresholds. */
const HANDLE_PX = 5
const HIT_PX = 10
const TOUCH_HIT_PX = 14
const SNAP_VERTEX_PX = 8
const SNAP_EDGE_PX = 6

export function AnnotationCanvas({ plateUrl, video, videoTime, className, minimal, onCursor, panArmed }: AnnotationCanvasProps) {
  const svgRef = useRef<SVGSVGElement | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const pointers = useRef(new Map<number, { x: number; y: number }>())
  const [boxWidth, setBoxWidth] = useState(1)
  const [snapHint, setSnapHint] = useState<SnapResult | null>(null)

  const vocab = useVocab()
  const doc = useEditorStore((s) => s.doc)
  const tool = useEditorStore((s) => s.tool)
  const toolState = useEditorStore((s) => s.toolState)
  const selection = useEditorStore((s) => s.selection)
  const hover = useEditorStore((s) => s.hover)
  const viewport = useEditorStore((s) => s.viewport)
  const snapping = useEditorStore((s) => s.snapping)
  const visibility = useEditorStore((s) => s.visibility)
  const solo = useEditorStore((s) => s.solo)
  const activeKind = useEditorStore((s) => s.activeKind)
  const proposal = useEditorStore((s) => s.proposal)

  const setToolState = useEditorStore((s) => s.setToolState)
  const setTool = useEditorStore((s) => s.setTool)
  const applyEffects = useEditorStore((s) => s.applyEffects)
  const setHover = useEditorStore((s) => s.setHover)

  /* ---------------------------------------------------------------------- */
  /* Measurements                                                            */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    const el = svgRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => setBoxWidth(Math.max(1, el.getBoundingClientRect().width)))
    ro.observe(el)
    setBoxWidth(Math.max(1, el.getBoundingClientRect().width))
    return () => ro.disconnect()
  }, [])

  /** Screen pixels per percent of plate width, at zoom 1. */
  const pxPerPct = boxWidth / 100
  const px = useCallback((screenPx: number) => pctTolerance(screenPx, viewport.k, pxPerPct), [viewport.k, pxPerPct])

  const clientToPct = useCallback(
    (clientX: number, clientY: number): Pt => {
      const el = svgRef.current
      if (!el) return [0, 0]
      const rect = el.getBoundingClientRect()
      const w = rect.width || 1
      const h = rect.height || w * ASPECT
      const x = (clientX - rect.left - viewport.tx) / viewport.k / w * 100
      const y = (clientY - rect.top - viewport.ty) / viewport.k / h * 100
      return [x, y]
    },
    [viewport],
  )

  /* ---------------------------------------------------------------------- */
  /* Snapping                                                                */
  /* ---------------------------------------------------------------------- */

  const rings: Ring[] = useMemo(() => {
    if (!isStageBody(doc)) return []
    return doc.regions.map((r) => r.poly.outer)
  }, [doc])

  const snapSources = useMemo(() => snapSourcesFromRings(rings), [rings])
  const horizonY = isStageBody(doc) && doc.depth.mode === 'horizon' ? doc.depth.horizon_y : undefined
  const edgeProbe = useEdgeProbe(plateUrl, snapping.image_edges && !video)

  const snap = useCallback(
    (pt: Pt, coarse: boolean): Pt => {
      const tol = { vertex: px(coarse ? TOUCH_HIT_PX : SNAP_VERTEX_PX), edge: px(coarse ? TOUCH_HIT_PX : SNAP_EDGE_PX) }
      const res = snapPoint(pt, { ...snapSources, horizonY, ...(edgeProbe ? { imageEdge: edgeProbe } : {}) }, snapping, tol)
      setSnapHint(res.kind === 'none' ? null : res)
      return res.pt
    },
    [px, snapSources, horizonY, snapping, edgeProbe],
  )

  /* ---------------------------------------------------------------------- */
  /* Tool plumbing                                                           */
  /* ---------------------------------------------------------------------- */

  const ctx: ReduceContext = useMemo(
    () => ({
      config: {
        ...DEFAULT_TOOL_CONFIG,
        vertexRadius: px(HIT_PX),
        edgeRadius: px(SNAP_EDGE_PX),
        closeRadius: px(HIT_PX + 2),
        dragThreshold: px(3),
        viewport,
        activeKind,
        selection: selection.ids,
        ringsById: (id: string) => ringOf(doc, id),
        horizonY,
        // What a hit id is and its kind: the way-in/out tool tells a spot from a door marker.
        typeOf: (id: string) => featureTypeOf(doc, id),
        kindOf: (id: string) => {
          const d = domainOfFeature(doc, id)
          return d ? featuresOf(doc, d).find((f) => f.id === id)?.kind : undefined
        },
      },
      viewport,
      armed: panArmed ?? false,
      pointers: pointers.current,
    }),
    [px, viewport, activeKind, selection.ids, doc, horizonY, panArmed],
  )

  const dispatch = useCallback(
    (ev: ToolEvent) => {
      const result = reduce({ tool, state: toolState }, ev, ctx)
      if (result.machine.tool !== tool) setTool(result.machine.tool)
      if (result.machine.state !== toolState) setToolState(result.machine.state)
      if (result.effects.length) applyEffects(result.effects)
    },
    [tool, toolState, ctx, setTool, setToolState, applyEffects],
  )

  /* ---------------------------------------------------------------------- */
  /* DOM events                                                              */
  /* ---------------------------------------------------------------------- */

  const makePointerEvent = useCallback(
    (t: 'pointerdown' | 'pointermove' | 'pointerup' | 'dblclick', e: React.PointerEvent<SVGSVGElement> | React.MouseEvent<SVGSVGElement>, hit: HitTarget): ToolEvent => {
      const coarse = 'pointerType' in e && e.pointerType !== 'mouse'
      const raw = clientToPct(e.clientX, e.clientY)
      const at = t === 'pointerup' || t === 'dblclick' ? raw : snap(raw, coarse)
      return {
        t,
        at,
        screen: { x: e.clientX, y: e.clientY },
        pointerId: 'pointerId' in e ? e.pointerId : 0,
        button: e.button,
        shift: e.shiftKey,
        alt: e.altKey,
        mod: e.ctrlKey || e.metaKey,
        coarse,
        hit,
      } as ToolEvent
    },
    [clientToPct, snap],
  )

  const onPointerDown = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
      e.currentTarget.setPointerCapture?.(e.pointerId)
      dispatch(makePointerEvent('pointerdown', e, hitFrom(e.target)))
    },
    [dispatch, makePointerEvent],
  )

  const onPointerMove = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (pointers.current.has(e.pointerId)) pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
      const pct = clientToPct(e.clientX, e.clientY)
      onCursor?.(pct)
      if (pointers.current.size === 0) {
        const hit = hitFrom(e.target)
        if (hit.id !== hover.id || hit.vertex !== hover.vertex || hit.edge !== hover.edge) setHover(hit)
      }
      dispatch(makePointerEvent('pointermove', e, hitFrom(e.target)))
    },
    [clientToPct, dispatch, hover.edge, hover.id, hover.vertex, makePointerEvent, onCursor, setHover],
  )

  const endPointer = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      dispatch(makePointerEvent('pointerup', e, hitFrom(e.target)))
      pointers.current.delete(e.pointerId)
      e.currentTarget.releasePointerCapture?.(e.pointerId)
      setSnapHint(null)
    },
    [dispatch, makePointerEvent],
  )

  const onPointerCancel = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      pointers.current.delete(e.pointerId)
      setSnapHint(null)
      dispatch({ t: 'pointercancel', pointerId: e.pointerId })
    },
    [dispatch],
  )

  const onDoubleClick = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      dispatch(makePointerEvent('dblclick', e, hitFrom(e.target)))
    },
    [dispatch, makePointerEvent],
  )

  /*
   * Wheel = zoom about the pointer. Bound natively with `passive: false` so it can
   * `preventDefault()`: React's `onWheel` is passive, so the page scrolled while the picture
   * zoomed, and a trackpad pinch (ctrl + wheel) zoomed the whole browser page instead. The
   * viewport is read from the store inside the handler, so it is bound once.
   */
  useEffect(() => {
    const el = svgRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const rect = el.getBoundingClientRect()
      const vp = useEditorStore.getState().viewport
      const dy = wheelPixels(e.deltaY, e.deltaMode, rect.height || 800) * (e.ctrlKey ? 4 : 1)
      useEditorStore.getState().setViewport(zoomAbout(vp, zoomStep(vp.k, dy), e.clientX - rect.left, e.clientY - rect.top))
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [])

  /* Live plate scrubbing --------------------------------------------------- */
  useEffect(() => {
    const el = videoRef.current
    if (!el || videoTime === undefined) return
    if (Math.abs(el.currentTime - videoTime) > 0.05) el.currentTime = videoTime
  }, [videoTime])

  /* ---------------------------------------------------------------------- */
  /* Render                                                                  */
  /* ---------------------------------------------------------------------- */

  const visible = useCallback((id: string) => (solo ? solo === id : (visibility[id] ?? true)), [solo, visibility])
  const lintErrors = useMemo(() => {
    const set = new Set<string>()
    if (isStageBody(doc)) for (const l of doc.lints) if (l.severity === 'error' && l.ref) set.add(l.ref)
    return set
  }, [doc])

  const selectedId = selection.ids[0]
  const selectedRing = selectedId ? ringOf(doc, selectedId) : undefined
  const drawingInvalid =
    toolState.t === 'polygon.drawing' && toolState.points.length >= 3 ? !isSimple([...toolState.points, toolState.cursor]) : false

  const transform = `translate(${(viewport.tx / Math.max(1, boxWidth)) * 100} ${(viewport.ty / Math.max(1, boxWidth)) * 100}) scale(${viewport.k})`
  const cursor = panArmed ? 'grab' : TOOL_BY_ID[tool].cursor
  /** The drawing preview wears the colour of the kind the active tool will stamp. */
  const previewDomain = domainOfTool(tool) ?? 'region_kind'

  return (
    <svg
      ref={svgRef}
      className={cn('block h-full w-full touch-none select-none bg-[var(--color-paper-3)]', className)}
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      preserveAspectRatio="xMidYMid meet"
      role="application"
      aria-label="Annotation canvas"
      style={{ cursor }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endPointer}
      onPointerCancel={onPointerCancel}
      onDoubleClick={onDoubleClick}
      onPointerLeave={() => onCursor?.(null)}
    >
      <g transform={transform}>
        {/* --- the plate ------------------------------------------------- */}
        {plateUrl ? (
          video ? (
            <foreignObject x={0} y={0} width={VIEW_W} height={VIEW_H}>
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <video
                ref={videoRef}
                src={plateUrl}
                muted
                playsInline
                preload="metadata"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </foreignObject>
          ) : (
            <image href={plateUrl} x={0} y={0} width={VIEW_W} height={VIEW_H} preserveAspectRatio="none" />
          )
        ) : (
          <rect x={0} y={0} width={VIEW_W} height={VIEW_H} fill="var(--color-paper-3, #222)" />
        )}

        {/* --- proposal ghosts under the live document -------------------- */}
        {proposal && proposal.view !== 'off' ? <ProposalLayer /> : null}

        {isStageBody(doc) ? (
          <>
            {doc.regions.map((r) => (
              <RegionShape
                key={r.id}
                id={r.id}
                kind={r.kind}
                label={r.label}
                poly={r.poly}
                colour={kindColour(vocab, r.kind, 'region_kind')}
                confidence={r.provenance.confidence}
                locked={r.provenance.locked}
                selected={selection.ids.includes(r.id)}
                hovered={hover.id === r.id}
                hidden={!visible(r.id)}
                hasError={lintErrors.has(r.id)}
              />
            ))}

            {doc.layers.map((l) => (
              <LayerShape
                key={l.id}
                id={l.id}
                z={l.z}
                baselineY={l.baseline_y}
                poly={layerOutline(doc, l.source)}
                colour={kindColour(vocab, 'walk_behind', 'region_kind')}
                selected={selection.ids.includes(l.id)}
                hovered={hover.id === l.id}
                label={l.id}
              />
            ))}

            {doc.paths.map((p) => (
              <PathShape
                key={p.id}
                id={p.id}
                kind={p.kind}
                points={p.points}
                closed={p.closed}
                colour={kindColour(vocab, p.kind, 'path_kind')}
                selected={selection.ids.includes(p.id)}
                hovered={hover.id === p.id}
                label={p.label}
              />
            ))}

            {doc.portals.map((p) => (
              <PortalMarker
                key={p.id}
                id={p.id}
                kind={p.kind}
                edge={p.edge}
                at={p.anchor_id ? doc.anchors.find((a) => a.id === p.anchor_id)?.at : undefined}
                linked={Boolean(p.anchor_id ?? p.to)}
                colour={kindColour(vocab, p.kind, 'portal_kind')}
                radius={px(HANDLE_PX * 1.6)}
                selected={selection.ids.includes(p.id)}
                hovered={hover.id === p.id}
                label={kindLabel(vocab, p.kind, 'portal_kind')}
              />
            ))}

            {doc.anchors.map((a) => (
              <AnchorMarker
                key={a.id}
                id={a.id}
                kind={a.kind}
                label={a.label}
                at={a.at}
                stand={a.stand}
                facing={a.facing}
                colour={kindColour(vocab, a.kind, 'anchor_kind')}
                radius={px(HANDLE_PX * 1.7)}
                selected={selection.ids.includes(a.id)}
                hovered={hover.id === a.id}
                locked={a.provenance.locked}
              />
            ))}

            <HorizonLine depth={doc.depth} active={tool === 'horizon' || toolState.t === 'horizon.dragging'} />
          </>
        ) : null}

        {/* --- handles and preview ---------------------------------------- */}
        {!minimal && selectedId && selectedRing ? (
          <VertexHandles
            id={selectedId}
            ring={selectedRing}
            radius={px(HANDLE_PX)}
            selectedIndex={selection.vertex?.index}
            hoverIndex={hover.id === selectedId ? hover.vertex : undefined}
            hoverEdge={hover.id === selectedId ? hover.edge : undefined}
            showMidpoints={toolState.t === 'idle'}
            colour="var(--color-accent)"
          />
        ) : null}

        {!minimal ? (
          <ToolPreview state={toolState} colour={kindColour(vocab, activeKind[SLOT_OF[previewDomain]], previewDomain)} radius={px(HANDLE_PX)} invalid={drawingInvalid} />
        ) : null}

        {snapHint ? (
          <circle
            cx={snapHint.pt[0]}
            cy={snapHint.pt[1] * ASPECT}
            r={px(HANDLE_PX) * 1.8}
            fill="none"
            stroke="var(--color-accent)"
            strokeWidth={px(HANDLE_PX) * 0.3}
            pointerEvents="none"
          />
        ) : null}
      </g>
    </svg>
  )
}

/**
 * Build the Sobel edge map for "snap to the picture" (§3.2 `edgeMap.worker`).
 *
 * The downscale to 480x270 happens here, on a `<canvas>`, because
 * `drawImage` is hardware accelerated and `OffscreenCanvas` is not available
 * everywhere; the worker only ever sees RGBA bytes. Everything degrades to
 * `null` — no worker, a tainted canvas, a plate that will not load — and the
 * toggle simply has no effect rather than throwing.
 */
function useEdgeProbe(plateUrl: string | undefined, enabled: boolean): ((p: Pt, tolPct: number) => Pt | null) | null {
  const [map, setMap] = useState<EdgeMap | null>(null)

  useEffect(() => {
    if (!enabled || !plateUrl || typeof Worker === 'undefined') {
      setMap(null)
      return
    }
    let cancelled = false
    let worker: Worker | null = null
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.decoding = 'async'

    img.onload = () => {
      if (cancelled) return
      try {
        const canvas = document.createElement('canvas')
        canvas.width = EDGE_W
        canvas.height = EDGE_H
        const ctx = canvas.getContext('2d', { willReadFrequently: true })
        if (!ctx) return
        ctx.drawImage(img, 0, 0, EDGE_W, EDGE_H)
        const data = ctx.getImageData(0, 0, EDGE_W, EDGE_H)
        worker = new Worker(new URL('../../geo/edgeMap.worker.ts', import.meta.url), { type: 'module' })
        worker.onmessage = (ev: MessageEvent<BuildResponse>) => {
          if (cancelled || ev.data?.type !== 'edges') return
          setMap({ w: ev.data.w, h: ev.data.h, mag: new Float32Array(ev.data.mag), gy: new Float32Array(ev.data.gy) })
        }
        const buffer = data.data.buffer as ArrayBuffer
        worker.postMessage({ type: 'build', id: 1, w: EDGE_W, h: EDGE_H, rgba: buffer }, [buffer])
      } catch {
        setMap(null)
      }
    }
    img.onerror = () => setMap(null)
    img.src = plateUrl

    return () => {
      cancelled = true
      worker?.terminate()
    }
  }, [enabled, plateUrl])

  return useMemo(() => (map ? makeEdgeProbe(map) : null), [map])
}

/** The outline a layer borrows from a region, when its source is one. */
function layerOutline(doc: StageBody, source: { key: string } | { region_id: string }) {
  if (!('region_id' in source)) return undefined
  return doc.regions.find((r) => r.id === source.region_id)?.poly
}

/** Read the `data-*` stamps the shape components leave on their elements. */
export function hitFrom(target: EventTarget | null): HitTarget {
  const el = target instanceof Element ? target.closest('[data-id],[data-horizon]') : null
  if (!el) return {}
  if (el.hasAttribute('data-horizon') && !el.hasAttribute('data-id')) return { horizon: true }
  const hit: HitTarget = {}
  const id = el.getAttribute('data-id')
  if (id) hit.id = id
  const vertex = el.getAttribute('data-vertex')
  if (vertex !== null) hit.vertex = Number(vertex)
  const edge = el.getAttribute('data-edge')
  if (edge !== null) hit.edge = Number(edge)
  const baseline = el.getAttribute('data-baseline')
  if (baseline) hit.baseline = baseline
  return hit
}

/** Wheel delta in pixels whatever the device reports (lines on Firefox, pages on some mice). */
function wheelPixels(deltaY: number, deltaMode: number, pageHeight: number): number {
  if (deltaMode === 1) return deltaY * 16
  if (deltaMode === 2) return deltaY * pageHeight
  return deltaY
}

const TYPE_OF_DOMAIN = { region_kind: 'region', anchor_kind: 'anchor', portal_kind: 'portal', path_kind: 'path' } as const

/** `region` / `anchor` / `portal` / `path` / `layer` for a feature id of the document. */
function featureTypeOf(doc: EditorDoc | null, id: string): FeatureType | undefined {
  const d = domainOfFeature(doc, id)
  if (d) return TYPE_OF_DOMAIN[d]
  return isStageBody(doc) && doc.layers.some((l) => l.id === id) ? 'layer' : undefined
}
